import { useTranslation } from "react-i18next";

import {
  columnSizingFeature,
  flexRender,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  tableFeatures,
  useTable,
  type Column,
  type PaginationState,
  type RowData,
  type RowSelectionState,
  type SortingState,
  type ColumnDef as TanstackColumnDef,
} from "@tanstack/react-table";
import { ChevronDown, ChevronsUpDown, ChevronUp } from "lucide-react";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";

import { DataTablePagination } from "./DataTablePagination";
import { DataTableSkeleton } from "./DataTableSkeleton";
import { DataTableToolbar } from "./DataTableToolbar";

// The only feature set the app uses; column defs and cell contexts are typed
// against it so views never import from @tanstack/react-table directly.
const features = tableFeatures({
  columnSizingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
});

export type ColumnDef<TData extends RowData> = TanstackColumnDef<
  typeof features,
  TData
>;

export interface DataTableProps<TData extends RowData> {
  data: TData[];
  columns: ColumnDef<TData>[];
  totalCount: number;
  isLoading?: boolean;
  emptyLabel?: string;

  sorting: SortingState;
  onSortingChange: (s: SortingState) => void;
  pagination: PaginationState;
  onPaginationChange: (p: PaginationState) => void;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (s: RowSelectionState) => void;

  getRowId: (row: TData) => string;

  searchValue?: string;
  onSearchChange?: (v: string) => void;
  searchPlaceholder?: string;
  filterSlot?: React.ReactNode;
  onResetFilters?: () => void;
  bulkActionsSlot?: React.ReactNode;
  onRowClick?: (row: TData, e: React.MouseEvent) => void;
  /** Row id (per `getRowId`) of the row currently open in a detail pane, if any.
   *  That row gets an active highlight + `aria-current`. This is distinct from the
   *  checkbox `rowSelection` used for bulk actions. */
  selectedRowId?: string | null;
}

function SortableHeaderButton<TData extends RowData>({
  column,
  children,
}: {
  column: Column<typeof features, TData, unknown>;
  children: React.ReactNode;
}) {
  const sorted = column.getIsSorted();
  const Icon =
    sorted === "asc"
      ? ChevronUp
      : sorted === "desc"
        ? ChevronDown
        : ChevronsUpDown;
  return (
    <button
      type="button"
      onClick={() => column.toggleSorting(sorted === "asc")}
      className={cn(
        "hover:text-foreground flex items-center gap-1.5 text-left font-medium",
        sorted ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {children}
      <Icon
        className={cn("size-3.5 shrink-0", !sorted && "opacity-50")}
        aria-hidden
      />
    </button>
  );
}

export function DataTable<TData extends RowData>(props: DataTableProps<TData>) {
  const { t } = useTranslation();
  const table = useTable({
    features,
    data: props.data,
    columns: props.columns,
    state: {
      sorting: props.sorting,
      rowSelection: props.rowSelection ?? {},
      pagination: props.pagination,
    },
    onSortingChange: (updater) =>
      props.onSortingChange(
        typeof updater === "function" ? updater(props.sorting) : updater,
      ),
    onRowSelectionChange: (updater) => {
      if (!props.onRowSelectionChange) return;
      const next =
        typeof updater === "function"
          ? updater(props.rowSelection ?? {})
          : updater;
      props.onRowSelectionChange(next);
    },
    onPaginationChange: (updater) =>
      props.onPaginationChange(
        typeof updater === "function" ? updater(props.pagination) : updater,
      ),
    // Sorting is opt-in: a column is only sortable when it explicitly sets
    // `enableSorting: true`. This keeps headers from looking clickable unless
    // the backing endpoint actually supports `?ordering=` on that column.
    defaultColumn: { enableSorting: false },
    manualSorting: true,
    manualPagination: true,
    getRowId: props.getRowId,
  });

  const pageCount = Math.max(
    1,
    Math.ceil(props.totalCount / props.pagination.pageSize),
  );

  return (
    <div className="space-y-3">
      {props.bulkActionsSlot}

      {(props.onSearchChange || props.filterSlot) && (
        <DataTableToolbar
          search={props.searchValue ?? ""}
          onSearchChange={props.onSearchChange}
          searchPlaceholder={props.searchPlaceholder}
          filters={props.filterSlot}
          onReset={props.onResetFilters}
        />
      )}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((hg) => (
            <TableRow key={hg.id}>
              {hg.headers.map((h) => {
                const content = h.isPlaceholder
                  ? null
                  : flexRender(h.column.columnDef.header, h.getContext());
                return (
                  <TableHead
                    key={h.id}
                    style={{ width: h.getSize() || undefined }}
                  >
                    {h.column.getCanSort() ? (
                      <SortableHeaderButton column={h.column}>
                        {content}
                      </SortableHeaderButton>
                    ) : (
                      content
                    )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {props.isLoading ? (
            <DataTableSkeleton
              rows={Math.min(props.pagination.pageSize, 10)}
              columns={props.columns.length}
            />
          ) : table.getRowModel().rows.length === 0 ? (
            <TableRow>
              <TableCell
                colSpan={props.columns.length}
                className="h-24 text-center"
              >
                {props.emptyLabel ?? t("common.noResults")}
              </TableCell>
            </TableRow>
          ) : (
            table.getRowModel().rows.map((row) => {
              const isActive =
                props.selectedRowId != null && row.id === props.selectedRowId;
              return (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && "selected"}
                  data-active={isActive || undefined}
                  aria-current={isActive ? "true" : undefined}
                  onClick={(e) => props.onRowClick?.(row.original, e)}
                  className={cn(
                    props.onRowClick && "cursor-pointer",
                    isActive && "bg-accent hover:bg-accent",
                  )}
                >
                  {row.getAllCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext(),
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              );
            })
          )}
        </TableBody>
      </Table>

      <DataTablePagination
        pageIndex={props.pagination.pageIndex}
        pageSize={props.pagination.pageSize}
        pageCount={pageCount}
        totalCount={props.totalCount}
        onPageChange={(pageIndex) =>
          props.onPaginationChange({ ...props.pagination, pageIndex })
        }
        onPageSizeChange={(pageSize) =>
          props.onPaginationChange({ pageIndex: 0, pageSize })
        }
      />
    </div>
  );
}
